import type { SatelliteFeed } from '../api'
import { formatNumber, getLang, t } from '../i18n'

/** Состояние потока позиций: живой / с задержкой / молчит / сопоставить нечем. */
export type FeedState = 'live' | 'lagging' | 'silent' | 'unattributed'

export interface FeedLabel {
  state: FeedState
  /** Строка состояния с возрастом последней позиции. */
  text: string
  /** Объём за окно — отдельной строкой; у непривязанного потока null. */
  volume: string | null
}

// Границы состояний по возрасту последней позиции. Сутки — потому что прямой
// поток провайдера near-real-time и суточная пауза уже аномалия; неделя —
// окно, за которое считаются объёмы (дальше сравнивать не с чем).
const LIVE_MAX_HOURS = 24
const LAGGING_MAX_HOURS = 24 * 7

/** «6 ч назад» / «2 сут назад»: до двух суток счёт в часах, дальше в сутках —
 *  точность до часа на недельной шкале не значит ничего. */
function ageText(ageHours: number): string {
  if (ageHours < 48) return t('models.vessel.hoursAgo', { count: Math.round(ageHours) })
  return t('models.vessel.daysAgo', { count: Math.round(ageHours / 24) })
}

/** Разряды пробелами: «125 928» читается, «125928» — нет. */
function grouped(value: number): string {
  return formatNumber(getLang(), value, 0)
}

/** Поток созвездия → подпись для карточки аппарата.
 *
 *  ``null`` означает НЕ «данные не идут», а «привязать поток к созвездию
 *  нечем»: GFW агрегирует чужие группировки и спутник-источник не раскрывает.
 *  Путать эти два случая нельзя — первый был бы обвинением оператору.
 */
export function feedLabel(feed: SatelliteFeed | null | undefined): FeedLabel {
  if (!feed) {
    return {
      state: 'unattributed',
      text: t('satellites.feed.unattributed'),
      volume: null,
    }
  }

  const volume = t('satellites.feed.volume', {
    vessels: grouped(feed.vessels_7d),
    positions: grouped(feed.positions_7d),
  })

  if (feed.age_hours == null) {
    return { state: 'silent', text: t('satellites.feed.neverAny'), volume }
  }

  const age = ageText(feed.age_hours)
  if (feed.age_hours <= LIVE_MAX_HOURS) {
    return { state: 'live', text: t('satellites.feed.live', { age }), volume }
  }
  if (feed.age_hours <= LAGGING_MAX_HOURS) {
    return { state: 'lagging', text: t('satellites.feed.lagging', { age }), volume }
  }
  return { state: 'silent', text: t('satellites.feed.silent', { age }), volume }
}
